/**
 * TanStack Query hooks for the ownership-transfer review queue.
 *
 * Keys are built by {@link ownershipTransferKeys} rather than written inline, because a
 * key assembled ad-hoc at two call sites is two caches: the drawer badge and the page
 * would each fetch, and an apply would invalidate only one of them. Every key here is a
 * plain array of primitives and one params object, which react-query hashes with sorted
 * keys — so an inline literal is stable, but a value computed per render (a new array, a
 * `Date.now()`) would not be. Don't add one.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { invalidateComplaintsAfterTransfer } from '@/hooks/use-complaints';
import { queryKeys } from '@/hooks/use-data';
import { accountingService } from '@/services/accounting-service';
import {
	ownershipTransferService,
	type ApplyOwnershipTransferBody,
	type CreateAccountFromClosingBody,
	type OwnershipTransferListParams,
	type OwnershipTransferTransactionType,
	type ReviewExpectation,
	type TransferFromAccountBody,
} from '@/services/ownership-transfer-service';
import type { Account } from '@/types/models';

const STALE = 30 * 1000;

export const ownershipTransferKeys = {
	all: queryKeys.ownershipTransfers.all,
	list: (params: OwnershipTransferListParams) => [...ownershipTransferKeys.all, 'list', params] as const,
	detail: (id: string) => [...ownershipTransferKeys.all, 'detail', id] as const,
	pendingCount: queryKeys.ownershipTransfers.pendingCount,
	accountOptions: () => [...ownershipTransferKeys.all, 'account-options'] as const,
	builders: () => [...ownershipTransferKeys.all, 'builders'] as const,
};

export function useOwnershipTransfers(
	params: OwnershipTransferListParams = {},
	options: { enabled?: boolean } = {},
) {
	return useQuery({
		queryKey: ownershipTransferKeys.list(params),
		queryFn: () => ownershipTransferService.list(params),
		enabled: options.enabled ?? true,
		staleTime: STALE,
	});
}

export function useOwnershipTransfer(id: string | undefined) {
	return useQuery({
		queryKey: ownershipTransferKeys.detail(id ?? ''),
		queryFn: () => ownershipTransferService.get(id!),
		enabled: !!id,
		staleTime: STALE,
	});
}

/**
 * The nav badge's number: rows still sitting in someone's queue.
 *
 * Its own `/count` endpoint rather than a `limit: 1` list read — the badge renders on
 * every screen the drawer is mounted on, and it must not drag a page of rows behind it.
 * It counts `status = 'new'` regardless of what the matcher concluded, which is exactly
 * the set the page's default chip opens; a badge that disagrees with the page it links
 * to reads as a broken page.
 */
export function usePendingOwnershipTransferCount(options: { enabled?: boolean } = {}) {
	const query = useQuery({
		queryKey: ownershipTransferKeys.pendingCount(),
		queryFn: () => ownershipTransferService.pendingCount(),
		enabled: options.enabled ?? true,
		staleTime: STALE,
	});
	return { ...query, count: query.data?.pending ?? 0 };
}

/** `api/pagination.ts` clamps to 500. Asking for more silently returns 500. */
const ACCOUNT_PAGE_LIMIT = 500;
/** ~5,000 accounts is 10 pages; the cap only stops a bad `pages` spinning the tab. */
const MAX_ACCOUNT_PAGES = 40;

/**
 * Live accounts to offer when a row matched nothing, or matched several.
 *
 * Fetched ONLY while a picker is open (`enabled`), because it is the whole portfolio
 * roster and no other surface needs it. Closed and archived accounts are excluded
 * server-side by `status: 'active'` — a closed account is the seller's side of a
 * transfer that already happened, and offering one would transfer it a second time.
 *
 * PAGINATION IS EXHAUSTED, not requested away. A single `limit: 1000` read returned 500
 * rows of ~5,000, and `LocalTypeahead` filters in memory — so four accounts in five were
 * unreachable, and for an UNMATCHED packet this list is the only place an account can
 * come from. A reviewer who cannot find a real account concludes it does not exist and
 * dismisses a real transfer.
 *
 * A debounced `GET /accounts?q=` would be better still, but that route has no search
 * param today (`api/accounts.ts` reads associationId/status/buckets only) — adding one
 * is a server change. Ten short requests behind an explicitly-opened picker is the
 * version that can ship without one.
 */
export function useTransferAccountOptions(options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: ownershipTransferKeys.accountOptions(),
		queryFn: async () => {
			const data: Account[] = [];
			for (let page = 1; page <= MAX_ACCOUNT_PAGES; page++) {
				const res = await accountingService.listAccounts({
					status: 'active', limit: ACCOUNT_PAGE_LIMIT, page,
				});
				const rows = res?.data ?? [];
				data.push(...rows);
				// `pages` is authority; the empty-page check is the guard for a route that
				// stops sending pagination at all rather than an infinite loop.
				if (rows.length === 0 || page >= (res?.pagination?.pages ?? 1)) break;
			}
			return { data };
		},
		enabled: options.enabled ?? false,
		staleTime: STALE,
	});
}

/**
 * Invalidate the queue AND the accounting tree.
 *
 * An apply closes one account and opens another, so anything showing accounts, ledgers
 * or balances is stale the moment it returns — invalidating only the queue would leave
 * the seller's account rendered as live on every other screen.
 */
function useInvalidateOwnershipTransfers() {
	const qc = useQueryClient();
	return () => {
		void qc.invalidateQueries({ queryKey: ownershipTransferKeys.all });
		void qc.invalidateQueries({ queryKey: ['accounting'] });
		// An apply moves open complaints to the buyer (#566) — outside `accounting`.
		invalidateComplaintsAfterTransfer(qc);
	};
}

export function useApplyOwnershipTransfer() {
	const invalidate = useInvalidateOwnershipTransfers();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: ApplyOwnershipTransferBody }) =>
			ownershipTransferService.apply(id, body),
		onSuccess: () => invalidate(),
	});
}

/**
 * Settle a row stuck in `applying`.
 *
 * Invalidates the same wide net as apply, because one of the two outcomes RECORDS an
 * account transfer that already happened — so balances, ledgers and the accounts
 * themselves are as stale after this as they are after an apply.
 */
export function useSettleOwnershipTransfer() {
	const invalidate = useInvalidateOwnershipTransfers();
	return useMutation({
		mutationFn: ({ id, outcome, note, createdAccountId }: {
			id: string;
			outcome: 'applied' | 'not-applied';
			note: string;
			createdAccountId?: string;
		}) => ownershipTransferService.settle(id, outcome, note, createdAccountId),
		onSuccess: () => invalidate(),
	});
}

export function useDismissOwnershipTransfer() {
	const invalidate = useInvalidateOwnershipTransfers();
	return useMutation({
		mutationFn: ({ id, reason }: { id: string; reason: string }) => ownershipTransferService.dismiss(id, reason),
		onSuccess: () => invalidate(),
	});
}

/**
 * Finish a CLOSING by creating its new account. Invalidates the same net as apply, plus the
 * account typeahead's search cache (`accountSearch`), which lives outside `accounting` and
 * would otherwise not find the account that was just created.
 */
export function useCreateAccountFromClosing() {
	const invalidate = useInvalidateOwnershipTransfers();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: CreateAccountFromClosingBody }) =>
			ownershipTransferService.createAccount(id, body),
		onSuccess: () => {
			invalidate();
			void qc.invalidateQueries({ queryKey: ['accountSearch'] });
		},
	});
}

/**
 * Finish a closing as a transfer from the lot's existing account (#632). Same net as create-account:
 * it closes one account and opens another, and the typeahead must find the new one.
 */
export function useTransferFromAccount() {
	const invalidate = useInvalidateOwnershipTransfers();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: TransferFromAccountBody }) =>
			ownershipTransferService.transferFrom(id, body),
		onSuccess: () => {
			invalidate();
			void qc.invalidateQueries({ queryKey: ['accountSearch'] });
		},
	});
}

/** Mark a packet already handled by hand. Changes no account, so only the queue goes stale. */
export function useMarkOwnershipTransferHandled() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, accountId, note, expected }: {
			id: string; accountId: string; note?: string; expected?: ReviewExpectation;
		}) => ownershipTransferService.markHandled(id, accountId, note, expected),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ownershipTransferKeys.all });
		},
	});
}

/** A reviewer's Transfer ↔ Closing switch. Re-scored server-side, so the queue refetches. */
export function useSetTransactionType() {
	const invalidate = useInvalidateOwnershipTransfers();
	return useMutation({
		mutationFn: ({ id, transactionType }: { id: string; transactionType: OwnershipTransferTransactionType }) =>
			ownershipTransferService.setTransactionType(id, transactionType),
		onSuccess: () => invalidate(),
	});
}

/** The Builders list a packet seller is checked against to spot a closing. */
export function useTransferBuilders(options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: ownershipTransferKeys.builders(),
		queryFn: () => ownershipTransferService.listBuilders(),
		enabled: options.enabled ?? true,
		staleTime: STALE,
	});
}

/**
 * Builder writes change FUTURE detection only — queued rows are retyped by the next rescan,
 * not by this call — so only the builders list itself is invalidated. They DO restart the
 * rescan's matched pass: rows a half-finished pass already read were classified under the old
 * builder list, and resuming past them would never reclassify them.
 */
export function useCreateTransferBuilder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (name: string) => ownershipTransferService.createBuilder(name),
		onSuccess: () => {
			resetRescanCursor();
			void qc.invalidateQueries({ queryKey: ownershipTransferKeys.builders() });
		},
	});
}

export function useDeleteTransferBuilder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => ownershipTransferService.deleteBuilder(id),
		onSuccess: () => {
			resetRescanCursor();
			void qc.invalidateQueries({ queryKey: ownershipTransferKeys.builders() });
		},
	});
}

export function useResolveOwnershipTransferAccount() {
	const invalidate = useInvalidateOwnershipTransfers();
	return useMutation({
		mutationFn: ({ id, accountId }: { id: string; accountId: string }) =>
			ownershipTransferService.resolve(id, accountId),
		onSuccess: () => invalidate(),
	});
}

/**
 * Re-match every still-unresolved row, in short chunks until the server says it is done.
 *
 * The loop lives here rather than in the endpoint for the reason every bulk path in this
 * app gives: one long request dies under `wrangler dev` and takes its progress with it.
 * `MAX_PASSES` is a stop, not a budget — `rescanPending` stamps its durable per-row cursor
 * (`rescan_pass_at`) on every row it touches, resolved or not, so `done` is reachable even
 * for a permanently unmatchable packet and the cap only exists so a server bug cannot spin
 * a browser tab forever. Hitting the cap is not lost work either: the sweep is durable, so
 * a second click resumes where this one stopped rather than restarting it.
 *
 * The MATCHED-row pass is not durable server-side — it pages by a cursor the server hands
 * back (`matchedAfter` / `matchedDone`), and scanned rows stay eligible. So a run that stops
 * at the cap leaves its cursor in {@link resumeRescanCursor} and the next click picks it up;
 * restarting from `{}` instead would re-read the same prefix on every click and never finish.
 *
 * The cursor carries the builders version it was read under, and the SERVER discards it once
 * the builder list moves — so a builder edited while this loop is running is still honoured;
 * the loop's next call is simply the stale one. The reset on builder mutations only saves a call.
 */
let resumeRescanCursor: RescanCursor = {};

type RescanCursor = { matchedAfter?: string | null; matchedDone?: boolean; buildersVersion?: number | null };

/** Start the next rescan's matched pass from the top — the builder list it read is stale. */
function resetRescanCursor(): void {
	resumeRescanCursor = {};
}

export function useRescanOwnershipTransfers() {
	const invalidate = useInvalidateOwnershipTransfers();
	return useMutation({
		mutationFn: async () => {
			const MAX_PASSES = 20;
			let processed = 0;
			// Rows a concurrent sweep covered first. Carried through so a run that raced the
			// cron can say so, rather than reporting zero packets re-checked and looking broken.
			let skipped = 0;
			// Echo the server's cursor unchanged on every call — starting from wherever the last
			// unfinished click stopped. Module-level, so it survives the page remounting; a reload
			// starts fresh, which is only slower. It is a row id, not user data, so outliving a
			// sign-out costs nothing but a resumed (org-wide) sweep.
			let cursor: RescanCursor = resumeRescanCursor;
			for (let pass = 0; pass < MAX_PASSES; pass++) {
				const result = await ownershipTransferService.rescan(100, cursor);
				processed += result.processed;
				skipped += result.skipped ?? 0;
				cursor = {
					matchedAfter: result.matchedAfter ?? null,
					matchedDone: result.matchedDone ?? false,
					buildersVersion: result.buildersVersion ?? null,
				};
				if (result.done) {
					resumeRescanCursor = {};
					return { processed, skipped, done: true };
				}
				resumeRescanCursor = cursor;
			}
			return { processed, skipped, done: false };
		},
		onSuccess: () => invalidate(),
	});
}
